<template>
  <div class="messages">
    <el-alert
      v-for="message in combinedMessages"
      :key="message.id"
      :title="message.text"
      :type="message.type"
      show-icon>
    </el-alert>
  </div>
</template>

<script>
  import get from 'lodash.get';
  import log from '@/configured/logger';

  export default {
    name: 'Messages',
    data() {
      return {
        internalMessages: []
      }
    },
    props: {
      messages: {
        type: Array,
        default() { return []; }
      },
      request: Promise,
      errorMessages: {
        type: Object,
        default: null,
      },
      customErrorHandler: {
        type: Function,
        default: null,
      }
    },
    computed: {
      combinedMessages() { return [].concat(this.messages).concat(this.internalMessages); }
    },
    methods: {
      clearMessages() { this.internalMessages.splice(0, this.internalMessages.length); },
      checkResponseForCraftErrors,
      parseCraftErrors,
      parseSuccessMessages,
      isServerError,
    },
    watch: {
      request() {
        if(this.request) {
          this.clearMessages();
          this.request
            .then(this.checkResponseForCraftErrors)
            .catch(this.checkResponseForCraftErrors);
        }
        return [];
      }
    }
  }

  //////////

  function checkResponseForCraftErrors(response) {
    // If response is undefined/null (e.g. from a promise resolving with no value),
    // then there are no errors to parse from it.
    if (response === undefined || response === null) {
      // Do nothing, as there's no error information to process.
      return;
    }

    let craftErrors = this.parseCraftErrors(response);
    let successMessages = this.parseSuccessMessages(response);

    if (craftErrors.length > 0) {
      this.clearMessages();
      this.internalMessages = this.internalMessages.concat(craftErrors);
    } else if (successMessages.length > 0) {
      this.clearMessages();
      this.internalMessages = this.internalMessages.concat(successMessages);
    } else if (typeof response === 'object' && this.isServerError(response)) {
      if (this.customErrorHandler) {
        // Correctly push to internalMessages instead of an undefined 'errors' array
        const customErrorText = this.customErrorHandler({response});
        if (customErrorText) { // Ensure custom handler returned something
            this.internalMessages.push({
                text: customErrorText,
                type: 'error',
                id: new Date().getTime().toString()
            });
        }
      } else {
        // Optionally, add a generic server error message if no custom handler and no craftErrors
        // For example:
        // this.internalMessages.push({
        //     text: 'A server error occurred.',
        //     type: 'error',
        //     id: new Date().getTime().toString()
        // });
      }
    }
  }

  function isServerError(response) {
    // Add a check to ensure response is a valid object before accessing status
    return response && typeof response === 'object' && response.status >= 500;
  }

  function parseCraftErrors(data) {
    let errors = [];

    if (!data) {
      return errors;
    }

    // Robustly get the actual JSON payload
    const payload = data.response && data.response.data ? data.response.data : data.data ? data.data : data;



    if (payload && typeof payload === 'object') {
      if (payload.errorCode && this.errorMessages && this.errorMessages[payload.errorCode]) {
        errors.push({
          text: this.errorMessages[payload.errorCode],
          type: 'error',
          id: new Date().getTime().toString() + '-ec'
        });
      } else if (payload.error) { // Primary check for a single 'error' field
        const errorText = typeof payload.error === 'string' ? payload.error : JSON.stringify(payload.error);
        errors.push({
          text: this.customErrorHandler ? this.customErrorHandler({error: payload.error}) : errorText,
          type: 'error',
          id: new Date().getTime().toString() + '-se'
        });
      } else if (payload.message && typeof payload.message === 'string') { // Fallback for 'message' field
        // Don't treat success messages as errors
        if (!payload.message.toLowerCase().includes('sent') && !payload.message.toLowerCase().includes('success')) {
          errors.push({
            text: this.customErrorHandler ? this.customErrorHandler({message: payload.message}) : payload.message,
            type: 'error',
            id: new Date().getTime().toString() + '-sm'
          });
        }
      } else if (payload.errors && typeof payload.errors === 'object') { // Check for multiple field errors
        if (this.customErrorHandler) {
          errors.push({
            text: this.customErrorHandler(payload.errors),
            type: 'error',
            id: new Date().getTime().toString() + '-me'
          });
        } else {
          Object.keys(payload.errors).forEach((key, index) => {
            if (Array.isArray(payload.errors[key]) && payload.errors[key].length > 0) {
              errors.push({
                text: payload.errors[key][0], // Take the first error message for the field
                type: 'error',
                id: new Date().getTime().toString() + "-" + index + '-mfe'
              });
            }
          });
        }
      }
    } else if (typeof payload === 'string') { // If payload itself is a simple string error
        errors.push({
            text: payload,
            type: 'error',
            id: new Date().getTime().toString() + '-ps'
        });
    }

    if (errors.length > 0) {
        log.info("Messages.vue - Parsed errors:", errors);
    }

    return errors;
  }

  function parseSuccessMessages(data) {
    let successMessages = [];

    if (!data) {
      return successMessages;
    }

    // Robustly get the actual JSON payload
    const payload = data.response && data.response.data ? data.response.data : data.data ? data.data : data;

    if (payload && typeof payload === 'object') {
      // Check for success messages from Contact Form plugin
      if (payload.message && typeof payload.message === 'string') {
        const message = payload.message.toLowerCase();
        if (message.includes('sent') || message.includes('success')) {
          successMessages.push({
            text: payload.message,
            type: 'success',
            id: new Date().getTime().toString() + '-success'
          });
        }
      }
    }

    return successMessages;
  }
</script>

<style lang="scss" scoped>
  .messages {
    margin-top: 16px;
    margin-bottom: 16px;
  }

  .el-alert {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    // Enhanced styling for success messages
    &.el-alert--success {
      border-left: 4px solid #67c23a;
      background-color: #f0f9ff;
      
      .el-alert__title {
        color: #67c23a;
        font-weight: 600;
      }
    }

    // Enhanced styling for error messages
    &.el-alert--error {
      border-left: 4px solid #f56c6c;
      background-color: #fef0f0;
      
      .el-alert__title {
        color: #f56c6c;
        font-weight: 600;
      }
    }
  }
</style>
